// Nomad-mon Go! — ACCOUNTS UI (2026-10). Three surfaces, all owned here:
//   1. THE NAME FORGE (openNameForge): a new player types a name; every letter slams in as giant block
//      lettering with sparks, a shockwave and its own note; backspace shatters it. On confirm the account is
//      made (server) and SHAO roasts the name out loud (live LLM line + TTS, or a canned line on any failure),
//      with the line as a subtitle while the letters flinch and sag.
//   2. SIGN IN (openSignIn): "I already have an account" — name + 4-digit PIN, or this device's own pairing
//      code / QR for a signed-in device to approve (Menu -> LINK), or ask Jack for a link.
//   3. LINK (openLinkPage): optional, standalone. A one-time 10-minute login link (QR + send to myself),
//      approve another device's code, set / change a 4-digit PIN. Shown ONCE automatically on the first
//      return to the title after the player's first win, and any time from the menu's LINK item.
// Server: whatsapp-poster src/routes/nomadmon-accounts.ts. identity.js holds the token/state plumbing.
import * as audio from './audio.js';
import * as identity from './identity.js';
import ROAST from '../data/roast-canned.json' with { type: 'json' };

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
const NAME_MAX = 16;
let hooks = { notify: null, onSignedIn: null };
const $ = (id) => document.getElementById(id);
const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };

// ======================================================================= SOUND
// Synth cues through the shared sfx bus (mute + limiter apply). Each key is a soft kick + a click + a bell
// on a rising pentatonic step, so a name plays a little phrase; delete is a falling crumble.
const PENTA = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21, 24, 26, 28, 31, 33, 36];
function noiseBuf(ctx, secs) {
  const b = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * secs)), ctx.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return b;
}
function sfxKeySynth(i) {
  audio.playSynth('nf-key', (ctx, out, t0) => {
    const k = ctx.createOscillator(), kg = ctx.createGain();
    k.type = 'sine'; k.frequency.setValueAtTime(170, t0); k.frequency.exponentialRampToValueAtTime(48, t0 + 0.14);
    kg.gain.setValueAtTime(0.0001, t0); kg.gain.exponentialRampToValueAtTime(0.55, t0 + 0.004); kg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.2);
    k.connect(kg); kg.connect(out); k.start(t0); k.stop(t0 + 0.22);
    const n = ctx.createBufferSource(), hp = ctx.createBiquadFilter(), ng = ctx.createGain();
    n.buffer = noiseBuf(ctx, 0.05); hp.type = 'highpass'; hp.frequency.value = 2400;
    ng.gain.setValueAtTime(0.32, t0); ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.045);
    n.connect(hp); hp.connect(ng); ng.connect(out); n.start(t0);
    const f = 392 * Math.pow(2, PENTA[Math.min(i, PENTA.length - 1)] / 12);
    for (const [mult, g, d] of [[1, 0.2, 0.55], [2.01, 0.07, 0.3], [3.98, 0.03, 0.18]]) {
      const o = ctx.createOscillator(), og = ctx.createGain();
      o.type = mult === 1 ? 'triangle' : 'sine'; o.frequency.value = f * mult;
      og.gain.setValueAtTime(0.0001, t0); og.gain.exponentialRampToValueAtTime(g, t0 + 0.006); og.gain.exponentialRampToValueAtTime(0.0001, t0 + d);
      o.connect(og); og.connect(out); o.start(t0); o.stop(t0 + d + 0.02);
    }
    return t0 + 0.6;
  });
}
function sfxDeleteSynth() {
  audio.playSynth('nf-del', (ctx, out, t0) => {
    const o = ctx.createOscillator(), lp = ctx.createBiquadFilter(), g = ctx.createGain();
    o.type = 'sawtooth'; o.frequency.setValueAtTime(520, t0); o.frequency.exponentialRampToValueAtTime(70, t0 + 0.2);
    lp.type = 'lowpass'; lp.frequency.setValueAtTime(2600, t0); lp.frequency.exponentialRampToValueAtTime(300, t0 + 0.2);
    g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.24);
    o.connect(lp); lp.connect(g); g.connect(out); o.start(t0); o.stop(t0 + 0.26);
    const n = ctx.createBufferSource(), bp = ctx.createBiquadFilter(), ng = ctx.createGain();
    n.buffer = noiseBuf(ctx, 0.3); bp.type = 'bandpass'; bp.frequency.value = 900; bp.Q.value = 0.8;
    ng.gain.setValueAtTime(0.0001, t0); ng.gain.exponentialRampToValueAtTime(0.28, t0 + 0.02); ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.3);
    n.connect(bp); bp.connect(ng); ng.connect(out); n.start(t0);
    return t0 + 0.35;
  });
}
function sfxCharge() {
  audio.playSynth('nf-charge', (ctx, out, t0) => {
    const o = ctx.createOscillator(), g = ctx.createGain(), lp = ctx.createBiquadFilter();
    o.type = 'sawtooth'; o.frequency.setValueAtTime(90, t0); o.frequency.exponentialRampToValueAtTime(420, t0 + 0.6);
    lp.type = 'lowpass'; lp.frequency.setValueAtTime(400, t0); lp.frequency.exponentialRampToValueAtTime(5000, t0 + 0.6);
    g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(0.16, t0 + 0.5); g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.66);
    o.connect(lp); lp.connect(g); g.connect(out); o.start(t0); o.stop(t0 + 0.7);
    return t0 + 0.7;
  });
}
function sfxSlamSynth() {
  audio.playSynth('nf-slam', (ctx, out, t0) => {
    const k = ctx.createOscillator(), kg = ctx.createGain();
    k.type = 'sine'; k.frequency.setValueAtTime(120, t0); k.frequency.exponentialRampToValueAtTime(32, t0 + 0.5);
    kg.gain.setValueAtTime(0.0001, t0); kg.gain.exponentialRampToValueAtTime(0.9, t0 + 0.006); kg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.7);
    k.connect(kg); kg.connect(out); k.start(t0); k.stop(t0 + 0.72);
    const n = ctx.createBufferSource(), lp = ctx.createBiquadFilter(), ng = ctx.createGain();
    n.buffer = noiseBuf(ctx, 1.2); lp.type = 'lowpass'; lp.frequency.setValueAtTime(6000, t0); lp.frequency.exponentialRampToValueAtTime(200, t0 + 1.1);
    ng.gain.setValueAtTime(0.4, t0); ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.15);
    n.connect(lp); lp.connect(ng); ng.connect(out); n.start(t0);
    for (const [f, g] of [[196, 0.12], [293.7, 0.09], [392, 0.08], [587.3, 0.05]]) {
      const o = ctx.createOscillator(), og = ctx.createGain();
      o.type = 'triangle'; o.frequency.value = f;
      og.gain.setValueAtTime(0.0001, t0); og.gain.exponentialRampToValueAtTime(g, t0 + 0.02); og.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.6);
      o.connect(og); og.connect(out); o.start(t0); o.stop(t0 + 1.65);
    }
    return t0 + 1.7;
  });
}
function sfxNope() {
  audio.playSynth('nf-nope', (ctx, out, t0) => {
    for (const [dt, f] of [[0, 180], [0.13, 140]]) {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = 'square'; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t0 + dt); g.gain.exponentialRampToValueAtTime(0.08, t0 + dt + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.12);
      o.connect(g); g.connect(out); o.start(t0 + dt); o.stop(t0 + dt + 0.13);
    }
    return t0 + 0.3;
  });
}

// The real thing (scripts/gen-sfx-nameforge.mjs): a giant metal slab STAMPS each letter -- five takes,
// never the same one twice, slight pitch spread; a new stamp chokes the last one's ring so fast typing
// stays a rhythm, not a smear. The confirm is the forge-press slam; backspace pries a letter off.
// Until the buffers decode (the first key or two), the synth cues above stand in.
// v299 (Jack: "make the hammer sounds more epic"): five cinematic hammer-on-anvil takes, each layered with a
// synth sub boom (weight) and a tuned anvil ring that climbs a pentatonic step per letter (the name plays a phrase).
// v301 (Jack: "pathetic and puny"): each letter is the world's heaviest hammer -- 2 s takes whose boom and anvil ring
// are allowed to ring on under the next letter (a long fade, not a choke)
const NF_STAMPS = ['nfham1', 'nfham2', 'nfham3', 'nfham4', 'nfham5'];
const NF_SFX = [...NF_STAMPS, 'nfimpact', 'nfriser', 'nfscrape1', 'nfscrape2'];
let lastStamp = null, lastStampIdx = -1, lastScrape = 0;
function preloadForgeSfx() { audio.loadSfx(NF_SFX).catch(() => {}); }
function choke(src, secs = 0.05) {
  try { const t = src.context.currentTime; src.gainNode.gain.setTargetAtTime(0, t, secs / 3); src.stop(t + secs + 0.05); } catch { /* ended */ }
}
function sfxKey(i) {
  let k = Math.floor(Math.random() * NF_STAMPS.length);
  if (k === lastStampIdx) k = (k + 1 + Math.floor(Math.random() * (NF_STAMPS.length - 1))) % NF_STAMPS.length;
  const src = audio.play(NF_STAMPS[k], { gain: 1, rate: 0.95 + Math.random() * 0.07 });
  if (!src) { sfxKeySynth(i); return; }
  if (lastStamp) choke(lastStamp, 0.45);
  lastStamp = src; lastStampIdx = k;
  sfxAnvil(i);
}
function sfxAnvil(i) {
  audio.playSynth('nf-anvil', (ctx, out, t0) => {
    const f = 523.25 * Math.pow(2, PENTA[Math.min(i, PENTA.length - 1)] / 12);
    for (const [mult, g, d] of [[1, 0.07, 0.7], [2.76, 0.035, 0.42], [5.4, 0.018, 0.22]]) {
      const o = ctx.createOscillator(), og = ctx.createGain();
      o.type = 'sine'; o.frequency.value = f * mult;
      og.gain.setValueAtTime(0.0001, t0); og.gain.exponentialRampToValueAtTime(g, t0 + 0.004); og.gain.exponentialRampToValueAtTime(0.0001, t0 + d);
      o.connect(og); og.connect(out); o.start(t0); o.stop(t0 + d + 0.02);
    }
    return t0 + 0.75;
  });
}
function sfxDelete() {
  lastScrape = 1 - lastScrape;
  if (!audio.play(lastScrape ? 'nfscrape1' : 'nfscrape2', { gain: 0.8, rate: 0.96 + Math.random() * 0.08 })) sfxDeleteSynth();
}
function sfxSlam() {
  if (lastStamp) choke(lastStamp, 0.03);
  if (lastRiser) choke(lastRiser, 0.04);
  if (audio.play('nfimpact', { gain: 1 })) audio.playSynth('nf-slam-chord', (ctx, out, t0) => {
    for (const [f, g] of [[98, 0.1], [146.8, 0.07], [196, 0.06], [293.7, 0.04]]) {
      const o = ctx.createOscillator(), og = ctx.createGain();
      o.type = 'triangle'; o.frequency.value = f;
      og.gain.setValueAtTime(0.0001, t0); og.gain.exponentialRampToValueAtTime(g, t0 + 0.03); og.gain.exponentialRampToValueAtTime(0.0001, t0 + 2.4);
      o.connect(og); og.connect(out); o.start(t0); o.stop(t0 + 2.45);
    }
    return t0 + 2.5;
  });
  else sfxSlamSynth();
}
let lastRiser = null;
function sfxRise() {
  lastRiser = audio.play('nfriser', { gain: 0.85 });
  if (!lastRiser) sfxCharge();
}

// ======================================================================= STYLE
const CSS = `
#nameForge, .acct-page { position: fixed; inset: 0; z-index: 60; display: none; color: #f3ecda;
  font-family: var(--v2-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif); }
#nameForge.open, .acct-page.open { display: block; animation: acctIn .28s cubic-bezier(.2,.8,.3,1) both; }
#nameForge.acct-out, .acct-page.acct-out { animation: acctOut .22s ease-in both; pointer-events: none; }
@keyframes acctIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes acctOut { from { opacity: 1; } to { opacity: 0; } }
@media (prefers-reduced-motion: reduce) { #nameForge.open, .acct-page.open, #nameForge.acct-out, .acct-page.acct-out { animation-duration: .01s; } }
#nameForge { background: #05040c; touch-action: manipulation; }
#nameForge canvas { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
#nfInput { position: absolute; left: 50%; top: 40%; width: 2px; height: 2px; opacity: 0; border: 0; padding: 0;
  font-size: 16px; background: transparent; color: transparent; caret-color: transparent; outline: none; }
#nfTap { position: absolute; inset: 0; background: transparent; border: 0; cursor: text; }
.nf-ui { position: absolute; left: 0; right: 0; display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 0 16px; pointer-events: none; }
.nf-ui > * { pointer-events: auto; }
#nfHint { font-family: "Unbounded", var(--v2-ui, sans-serif); font-weight: 700; font-size: clamp(12px, 3.4vmin, 18px);
  letter-spacing: .32em; text-transform: uppercase; color: rgba(246,230,178,.72); padding-left: .32em;
  animation: nfPulse 2.2s ease-in-out infinite; pointer-events: none; text-align: center; }
#nfStatus { min-height: 18px; font-size: 12px; letter-spacing: .18em; text-transform: uppercase; color: #ff9f8a; text-align: center; }
#nfSugs { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
#nfSugs button { appearance: none; font: inherit; font-size: 13px; font-weight: 700; letter-spacing: .06em;
  color: #f6e6b2; background: rgba(20,16,36,.85); border: 1px solid rgba(240,196,99,.55); border-radius: 999px; padding: 8px 14px; cursor: pointer; }
#nfGo, #nfNext { appearance: none; cursor: pointer; font-family: "Unbounded", var(--v2-ui, sans-serif); font-weight: 800;
  font-size: clamp(14px, 3.8vmin, 19px); letter-spacing: .14em; text-transform: uppercase; color: #1a1104;
  background: linear-gradient(180deg, #fff2c4, #f0c463 55%, #d4952b); border: 0; border-radius: 16px;
  padding: 15px 30px; box-shadow: 0 0 40px -6px rgba(240,196,99,.75), inset 0 1px 0 rgba(255,255,255,.7);
  transition: transform .12s ease, opacity .25s ease; }
#nfGo:active, #nfNext:active { transform: scale(.95); }
#nfGo[hidden], #nfNext[hidden] { display: none; }
#nfGo:disabled, #nfNext:disabled { opacity: .45; }
#nfHave { appearance: none; background: none; border: 0; cursor: pointer; font: inherit; font-size: 13px; min-height: 44px;
  letter-spacing: .14em; text-transform: uppercase; color: #a79fd0; text-decoration: underline; text-underline-offset: 3px; padding: 8px 10px; }
#nfHave { position: absolute; top: max(12px, env(safe-area-inset-top)); right: 10px; }
#nfSub { max-width: min(92vw, 760px); text-align: center; font-family: "Unbounded", var(--v2-ui, sans-serif); font-weight: 600;
  font-size: clamp(17px, 3.9vmin, 27px); line-height: 1.38; color: #fff8e6; text-shadow: 0 2px 14px rgba(0,0,0,.95), 0 0 2px #000; min-height: 1.4em; }
#nfSub .w { opacity: 0; display: inline-block; transform: translateY(8px) scale(.86); transition: opacity .16s ease, transform .22s cubic-bezier(.2,1.6,.4,1); }
#nfSub .w.on { opacity: 1; transform: none; }
@keyframes nfPulse { 0%,100% { opacity: .45 } 50% { opacity: 1 } }

/* polB: the account pages (sign in, link, switch, account lost) in the OBSIDIAN look: ink + one violet glow,
   Geist in sentence case, the QR as the hero, one ivory primary per step, a segmented control between steps
   (each step slides in), 44px+ targets. The name forge above keeps its own gold forge look. */
.acct-page { background: radial-gradient(ellipse 90% 46% at 50% 0%, rgba(124,108,245,.22), transparent 72%), #060609;
  color: #f4f3f8; overflow-y: auto; -webkit-overflow-scrolling: touch; }
.acct-wrap { min-height: 100%; box-sizing: border-box; display: flex; flex-direction: column; align-items: center;
  gap: 16px; width: 100%; max-width: 520px; margin: 0 auto;
  padding: max(12px, env(safe-area-inset-top)) 16px max(24px, env(safe-area-inset-bottom)); }
.acct-page.open .acct-wrap { animation: acctRise .5s cubic-bezier(.22,1,.36,1) both; }
@keyframes acctRise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
.acct-top { width: 100%; min-height: 48px; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.acct-page .acct-link { appearance: none; min-height: 44px; min-width: 44px; padding: 0 14px; border: 0; border-radius: 999px;
  background: none; cursor: pointer; font: 600 15px/1 var(--v2-ui, sans-serif); letter-spacing: 0; text-transform: none;
  color: #b6b4c6; text-decoration: none; transition: transform .3s cubic-bezier(.34,1.56,.64,1); }
.acct-page .acct-top > .acct-link:first-child { padding: 0 18px 0 14px; color: #f4f3f8;
  background: rgba(255,255,255,.07); box-shadow: inset 0 0 0 1px rgba(255,255,255,.12); }
.acct-page .acct-link:active { transform: scale(.95); }
.acct-page .acct-link[hidden] { display: none; }
.acct-h { margin: 10px 0 0; text-align: center; font: 700 clamp(26px, 7vw, 34px)/1.15 var(--v2-ui, sans-serif);
  letter-spacing: -.03em; color: #f4f3f8; }
.acct-sub { margin: -6px 0 2px; max-width: 380px; text-align: center; font-size: 15px; line-height: 1.45; color: #b6b4c6; }
.acct-tabs { width: 100%; max-width: 400px; display: flex; gap: 4px; padding: 4px; box-sizing: border-box; border-radius: 16px;
  background: rgba(255,255,255,.06); box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); }
.acct-tab { flex: 1 1 0; min-height: 44px; border: 0; border-radius: 12px; background: none; cursor: pointer;
  font: 600 14px/1.1 var(--v2-ui, sans-serif); color: #b6b4c6; transition: background-color .25s ease, color .25s ease; }
.acct-tab.on { color: #f4f3f8; background: rgba(255,255,255,.13); box-shadow: inset 0 1px 0 rgba(255,255,255,.08); }
.acct-step { width: 100%; display: none; flex-direction: column; align-items: center; gap: 14px; }
.acct-step.on { display: flex; animation: acctStepIn .42s cubic-bezier(.22,1,.36,1) both; }
.acct-step.on.back { animation-name: acctStepBack; }
@keyframes acctStepIn { from { opacity: 0; transform: translateX(26px); } to { opacity: 1; transform: none; } }
@keyframes acctStepBack { from { opacity: 0; transform: translateX(-26px); } to { opacity: 1; transform: none; } }
.acct-card { width: 100%; max-width: 440px; box-sizing: border-box; display: flex; flex-direction: column; align-items: center;
  gap: 14px; padding: 26px 20px; border-radius: 26px; background: rgba(17,17,24,.94);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.12), 0 40px 90px -30px rgba(0,0,0,.95); }
.acct-card h3 { margin: 0; text-align: center; font: 700 22px/1.2 var(--v2-ui, sans-serif); letter-spacing: -.02em; color: #f4f3f8; }
.acct-card p, .acct-step p { margin: 0; max-width: 360px; text-align: center; font-size: 15px; line-height: 1.45; color: #b6b4c6; }
.acct-qr { position: relative; width: min(68vw, 272px); aspect-ratio: 1; box-sizing: border-box; padding: 14px; margin: 10px 0 4px;
  display: grid; place-items: center; border-radius: 26px; background: #fff;
  box-shadow: 0 0 0 1px rgba(255,255,255,.5), 0 30px 80px -26px rgba(154,140,255,.85);
  animation: acctQrIn .6s cubic-bezier(.34,1.56,.64,1) both; }
.acct-qr svg { width: 100%; height: 100%; display: block; }
.acct-qr::before { content: ''; position: absolute; inset: -9px; border-radius: 33px; pointer-events: none;
  box-shadow: 0 0 0 2px rgba(154,140,255,.55); opacity: 0; animation: acctQrPulse 2.6s ease-out 1s infinite; }
.acct-qr.empty { background: rgba(255,255,255,.06); color: #807d94; font-size: 14px;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.12); animation: acctQrWait 1.3s ease-in-out infinite alternate; }
.acct-qr.empty::before { content: none; }
@keyframes acctQrIn { from { opacity: 0; transform: scale(.86); } to { opacity: 1; transform: none; } }
@keyframes acctQrPulse { 0% { opacity: .9; transform: scale(1); } 70%, 100% { opacity: 0; transform: scale(1.07); } }
@keyframes acctQrWait { from { opacity: .45; } to { opacity: 1; } }
.acct-code { font: 700 clamp(26px, 7.5vw, 32px)/1 var(--v2-ui, sans-serif); letter-spacing: .1em; color: #f4f3f8;
  font-variant-numeric: tabular-nums; }
.acct-small { font-size: 13px; line-height: 1.4; letter-spacing: 0; text-transform: none; color: #807d94; text-align: center; }
.acct-ok { color: #9fe3c0 !important; }
.acct-err { min-height: 18px; font-size: 14px; color: #ffb88c; text-align: center; }
.acct-page .acct-btn { appearance: none; cursor: pointer; width: 100%; max-width: 360px; min-height: 58px; padding: 0 24px;
  border: 0; border-radius: 18px; background: #f3f2f7; color: #0c0b12;
  font: 700 18px/1 var(--v2-ui, sans-serif); letter-spacing: -.01em; text-transform: none;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.7), 0 18px 44px -18px rgba(255,210,150,.7);
  transition: transform .35s cubic-bezier(.34,1.56,.64,1), opacity .25s ease; }
.acct-page .acct-btn:active:not(:disabled) { transform: scale(.97); }
.acct-page .acct-btn:disabled { opacity: .45; cursor: default; }
.acct-btn2, .acct-skip { appearance: none; cursor: pointer; min-height: 48px; padding: 0 20px; border: 0; border-radius: 14px;
  background: rgba(255,255,255,.08); box-shadow: inset 0 0 0 1px rgba(255,255,255,.16); color: #f4f3f8;
  font: 600 15px/1 var(--v2-ui, sans-serif); letter-spacing: -.005em; text-transform: none;
  transition: transform .3s cubic-bezier(.34,1.56,.64,1); }
.acct-btn2:active:not(:disabled), .acct-skip:active { transform: scale(.96); }
.acct-btn2:disabled { opacity: .5; cursor: default; }
.acct-top .acct-skip { min-height: 44px; border-radius: 999px; }
.acct-net { display: inline-flex; align-items: center; gap: 12px; margin: 6px auto 0; padding: 6px 6px 6px 16px; border-radius: 999px;
  background: rgba(255,130,100,.1); box-shadow: inset 0 0 0 1px rgba(255,150,120,.4); color: #ffd2c2; font-size: 14px; line-height: 1.2;
  text-align: left; text-transform: none; letter-spacing: normal; animation: acctIn .25s ease-out both; }
.acct-net button { min-height: 44px; font: 650 14px/1 var(--v2-ui, sans-serif); color: #1a0f0a; background: #ffd2b8; border: 0;
  border-radius: 999px; padding: 0 18px; cursor: pointer; }
.acct-in { appearance: none; width: 100%; max-width: 320px; min-height: 56px; box-sizing: border-box; padding: 12px;
  font: 500 18px/1.2 var(--v2-ui, sans-serif); text-align: center; color: #fff; outline: none; border: 0; border-radius: 16px;
  background: rgba(255,255,255,.06); box-shadow: inset 0 0 0 1px rgba(255,255,255,.14); transition: box-shadow .2s ease; }
.acct-in::placeholder { color: #5a586c; }
.acct-in:focus { box-shadow: inset 0 0 0 2px rgba(154,140,255,.85); }
.acct-in[hidden] { display: none; }
.acct-pin { max-width: 240px; letter-spacing: .5em; padding-left: calc(12px + .5em); font: 700 28px/1 var(--v2-ui, sans-serif); }
.acct-row { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; align-items: center; }
.acct-here { width: 100%; max-width: 400px; }
.acct-warn { width: 100%; max-width: 400px; box-sizing: border-box; padding: 14px 16px; border-radius: 16px;
  background: rgba(255,140,110,.1); box-shadow: inset 0 0 0 1px rgba(255,150,120,.42);
  color: #ffd9cf; font-size: 14px; line-height: 1.45; text-align: center; }
.acct-warn b { color: #fff; }
#acctNet { position: fixed; top: max(10px, env(safe-area-inset-top)); left: 50%; z-index: 70; transform: translateX(-50%);
  display: inline-flex; align-items: center; gap: 8px; padding: 8px 14px 8px 12px; border-radius: 999px; pointer-events: none;
  font: 600 13px/1.1 var(--v2-ui, sans-serif); letter-spacing: 0; text-transform: none; white-space: nowrap; color: #ffd9cf;
  background: rgba(22,14,18,.9); box-shadow: inset 0 0 0 1px rgba(255,150,120,.42), 0 12px 30px -12px rgba(0,0,0,.9);
  -webkit-backdrop-filter: blur(14px); backdrop-filter: blur(14px); animation: acctNetIn .45s cubic-bezier(.22,1,.36,1) both; }
#acctNet::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: #ff8a6a;
  box-shadow: 0 0 10px rgba(255,138,106,.9); animation: acctNetDot 1.6s ease-in-out infinite alternate; }
#acctNet.out { animation: acctNetOut .24s ease-in both; }
#acctNet[hidden] { display: none; }
@keyframes acctNetIn { from { opacity: 0; transform: translate(-50%, -14px); } to { opacity: 1; transform: translate(-50%, 0); } }
@keyframes acctNetOut { from { opacity: 1; transform: translate(-50%, 0); } to { opacity: 0; transform: translate(-50%, -10px); } }
@keyframes acctNetDot { from { opacity: .45; } to { opacity: 1; } }
#acctLost { z-index: 90; display: grid; place-items: center; }
.acct-foot { font-size: 14px; color: #b6b4c6; text-align: center; max-width: 520px; line-height: 1.5; }
@media (prefers-reduced-motion: reduce) {
  .acct-page.open .acct-wrap, .acct-step.on, .acct-qr, .acct-qr::before, .acct-qr.empty, #acctNet, #acctNet::before, #acctNet.out { animation: none; }
}
`;
function injectStyle() {
  if ($('acctStyle')) return;
  const s = el('style'); s.id = 'acctStyle'; s.textContent = CSS; document.head.appendChild(s);
}

// ======================================================================= THE NAME FORGE
const ALLOWED = /[A-Za-z0-9 ._'-]/;
function sanitize(v) {
  let s = ''; for (const ch of String(v)) if (ALLOWED.test(ch)) s += ch;
  return s.replace(/^\s+/, '').replace(/\s{2,}/g, ' ').slice(0, NAME_MAX);
}
const validName = (s) => { const t = s.trim(); return t.length >= 2 && /[A-Za-z]/.test(t) && /^[A-Za-z0-9](?:[A-Za-z0-9 ._'-]*[A-Za-z0-9])?$/.test(t) && !/[ ._'-]{2}/.test(t); };

let forge = null;   // the open forge's state (one at a time)

/** Opens the name forge. onDone(name) once an account exists and Shao has spoken (or been skipped);
 *  onHaveAccount() for "I already have an account". Must be called from a user gesture (audio). */
export function openNameForge({ onDone, onHaveAccount } = {}) {
  injectStyle();
  audio.unlock();
  preloadForgeSfx();
  if (forge) return;
  const root = el('div'); root.id = 'nameForge'; root.className = 'open';
  root.setAttribute('role', 'dialog'); root.setAttribute('aria-label', 'Type your name');
  const canvas = el('canvas');
  const tap = el('button'); tap.id = 'nfTap'; tap.type = 'button'; tap.setAttribute('aria-label', 'Type your name');
  const input = el('input'); input.id = 'nfInput';
  Object.assign(input, { type: 'text', autocomplete: 'off', spellcheck: false, maxLength: NAME_MAX, enterKeyHint: 'go' });
  input.setAttribute('autocapitalize', 'words'); input.setAttribute('autocorrect', 'off'); input.setAttribute('aria-label', 'Your name');
  const have = el('button', null, 'Link an existing account'); have.id = 'nfHave'; have.type = 'button';
  const ui = el('div', 'nf-ui');
  const hint = el('div', null, 'Type your name'); hint.id = 'nfHint';
  const status = el('div'); status.id = 'nfStatus'; status.setAttribute('aria-live', 'polite');
  const sugs = el('div'); sugs.id = 'nfSugs';
  const go = el('button', null, "That's me"); go.id = 'nfGo'; go.type = 'button'; go.hidden = true;
  const sub = el('div'); sub.id = 'nfSub'; sub.setAttribute('aria-live', 'polite');
  const next = el('button', null, "Let's go"); next.id = 'nfNext'; next.className = 'acct-btn'; next.type = 'button'; next.hidden = true;
  ui.append(hint, status, sugs, go, sub, next);
  root.append(canvas, tap, input, ui, have);
  document.body.appendChild(root);

  const ctx = canvas.getContext('2d');
  const S = forge = {
    root, canvas, ctx, input, text: '', glyphs: [], parts: [], shards: [], rings: [], embers: [], smokes: [],
    shake: 0, flash: 0, punch: 0, push: 0, echo: 0, rays: 0, bgHeat: 0, combo: 0, charging: false, chargeT: 0, impactT: null,
    sag: 0, flinch: 0, phase: 'type', fs: 120, fsTarget: 120, W: 0, H: 0, dpr: 1, dprCap: 2,
    top: 0, vh: 0, raf: 0, last: performance.now(), t: 0, busy: false, closed: false,
  };
  for (let i = 0; i < (REDUCED ? 20 : 64); i++) S.embers.push(newEmber(true));

  const layout = () => {
    const vv = window.visualViewport;
    S.dpr = Math.min(S.dprCap, window.devicePixelRatio || 1);
    S.W = innerWidth; S.H = innerHeight;
    S.top = vv ? vv.offsetTop : 0; S.vh = vv ? vv.height : innerHeight;
    canvas.width = Math.round(S.W * S.dpr); canvas.height = Math.round(S.H * S.dpr);
    // the controls sit under the letters inside the visible viewport (above a phone keyboard)
    const lettersY = S.top + S.vh * (S.vh < 420 ? 0.36 : 0.40);
    ui.style.top = `${Math.round(lettersY + Math.min(S.vh * 0.2, S.W * 0.16) + 8)}px`;
    input.style.top = `${Math.round(lettersY)}px`;
    fitFont(S);
    buildBackdrop(S);
  };
  layout();
  const onResize = () => layout();
  addEventListener('resize', onResize);
  window.visualViewport?.addEventListener('resize', onResize);
  window.visualViewport?.addEventListener('scroll', onResize);

  try {
    if (sessionStorage.getItem('nomadmon-account-lost')) {
      status.textContent = 'Your account was reset · make a new one';
      sessionStorage.removeItem('nomadmon-account-lost');
    }
  } catch { /* private */ }
  const focus = () => { if (S.phase === 'type') { input.focus({ preventScroll: true }); } };
  tap.addEventListener('click', focus);
  setTimeout(focus, 60);
  focus();

  input.addEventListener('input', () => {
    if (S.phase !== 'type') { input.value = S.text; return; }
    const v = sanitize(input.value);
    if (v !== input.value) input.value = v;
    setText(S, v);
    status.textContent = ''; sugs.innerHTML = '';
    go.hidden = !validName(S.text);
    hint.style.display = S.text ? 'none' : '';
    // a pause on a valid name starts Shao writing, so his line is usually ready the moment ENTER lands
    clearTimeout(S.pfT);
    if (validName(S.text)) S.pfT = setTimeout(() => { const n = S.text.trim(); if (S.phase === 'type' && n !== S.pfName) { S.pfName = n; identity.roastPrefetch(n); } }, 900);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); if (!go.hidden) confirm(); }
  });
  go.addEventListener('click', () => confirm());
  have.addEventListener('click', () => { close(); onHaveAccount?.(); });

  async function confirm() {
    if (S.busy || S.phase !== 'type' || !validName(S.text)) return;
    S.busy = true; go.disabled = true; go.hidden = true; status.textContent = ''; sugs.innerHTML = '';
    const name = S.text.trim();
    // the name heats white and the camera pushes in while the account is made; Shao starts writing at once
    S.charging = true; S.chargeT = S.t; sfxRise();
    const roastP = identity.roastName(name).catch(() => null);
    let made = false, reason = null, netDown = false;
    await Promise.all([
      identity.createAccount(name).then(() => { made = true; }, (err) => { reason = err?.body || { code: err?.status ? 'HTTP' : 'OFFLINE' }; netDown = isNetErr(err); }),
      new Promise(r => setTimeout(r, REDUCED ? 0 : 800)),
    ]);
    go.disabled = false; S.busy = false;
    if (!made) { S.charging = false; if (lastRiser) choke(lastRiser, 0.08); go.hidden = !validName(S.text); }
    if (!made && reason.code === 'NAME_TAKEN') {
      nope(S); status.textContent = 'That name is taken';
      for (const s of (reason.suggestions || []).slice(0, 3)) {
        const b = el('button', null, s); b.type = 'button';
        b.addEventListener('click', () => { input.value = s; setText(S, s, true); go.hidden = !validName(s); status.textContent = ''; sugs.innerHTML = ''; focus(); });
        sugs.appendChild(b);
      }
      focus();
      return;
    }
    if (!made && reason.code === 'NAME_REJECTED') { await roast(name, true); return; }
    if (!made && netDown) { nope(S); netErr(status, () => confirm()); focus(); return; }
    if (!made) {
      nope(S);
      status.textContent = reason.code === 'NAME_INVALID' ? (reason.error || 'Letters and numbers only')
        : reason.status === 429 || reason.code === 'RATE_LIMITED' ? 'Too many new names from here. Try later'
        : "Can't reach the arena. Try again";
      focus();
      return;
    }
    await roast(name, false, roastP);
  }

  // SHAO speaks. Offensive: he roasts the attempt (never repeating it), the letters shatter, typing resumes.
  async function roast(name, offensive, roastP = null) {
    S.phase = 'roast'; input.blur(); go.hidden = true; hint.style.display = 'none'; have.hidden = true;
    slam(S); sfxSlam();
    const canned = (list) => list[Math.floor(Math.random() * list.length)];
    let line = null, nameAudio = null;
    if (!offensive) {
      // the forge keeps hammering the name while Shao finishes writing and recording
      const hammer = setInterval(() => aftershock(S), 1150);
      try {
        const r = await (roastP || identity.roastName(name)).finally(() => clearInterval(hammer));
        if (!r) throw new Error('no roast');
        if (r.kind === 'offensive') offensive = true;
        else if (!r.fallback && r.text) line = { text: r.text, file: r.audio };
        else nameAudio = r.nameAudio || null;
      } catch { /* timeout / offline: canned */ }
    }
    // a canned line, said after Shao says the name when the server could voice it
    if (!line) {
      const c = canned(offensive ? ROAST.offensive : ROAST.generic);
      line = { text: nameAudio ? `${name}. ${c.text}` : c.text, file: `./${c.file}`, dur: c.dur && c.dur + (nameAudio ? 1 : 0), pre: nameAudio };
    }
    // the account exists: LET'S GO is live at once (Astra r1: never hold a new player hostage to a ~20 s roast);
    // tapping it stops Shao and leaves. The roast plays for whoever wants it.
    let left = false;
    if (!offensive) armNext(() => { left = true; audio.voStop?.(); });
    await speak(line);
    if (left) return;
    if (offensive) {
      shatterAll(S); S.sag = 0; S.phase = 'type'; have.hidden = false; S.raysOn = false; S.impactT = null;
      sub.innerHTML = ''; status.textContent = 'Pick another name';
      input.value = ''; setText(S, '', false); hint.style.display = '';
      focus();
      return;
    }
    // (LET'S GO was armed before the line; after it ends, a tap anywhere also leaves)
    setTimeout(() => { if (!S.closed) root.addEventListener('pointerdown', tapAnywhere); }, 300);
  }
  let tapAnywhere = null;
  function armNext(onLeave) {
    next.hidden = false;
    next.focus({ preventScroll: true });
    const finish = () => {
      next.removeEventListener('click', finish); if (tapAnywhere) root.removeEventListener('pointerdown', tapAnywhere);
      onLeave?.(); flyOut(S); setTimeout(() => { close(); onDone?.(identity.currentPlayer()); }, REDUCED ? 60 : 420);
    };
    tapAnywhere = (e) => { if (e.target !== next) finish(); };
    next.addEventListener('click', finish);
  }

  function speak(line) {
    return new Promise((resolve) => {
      sub.innerHTML = '';
      const words = line.text.split(/\s+/).filter(Boolean).map((w, i) => {
        const s = el('span', 'w', w); sub.appendChild(s);
        sub.appendChild(document.createTextNode(' '));
        return s;
      });
      const est = line.dur || Math.max(2.2, words.length * 0.36 + 0.6);
      const t0 = performance.now();
      let ended = false, durS = est;
      const done = () => { if (ended) return; ended = true; for (const w of words) w.classList.add('on'); setTimeout(resolve, 450); };
      // words reveal across the line's length; every word makes the letters flinch, the name sags as he goes
      const tick = () => {
        if (S.closed) return resolve();
        const p = (performance.now() - t0) / 1000 / durS;
        const n = Math.min(words.length, Math.floor(p * words.length * 1.08) + 1);
        for (let i = 0; i < n; i++) if (!words[i].classList.contains('on')) { words[i].classList.add('on'); S.flinch = 1; }
        S.sag = Math.min(1, p);
        if (!ended && p < 1.6) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      audio.voFilePreload?.(line.file);
      let safety = 0;
      const main = () => audio.voFile(line.file, { gain: 1.05, interrupt: true, duck: 0.08, onEnd: () => done() })
        .then((started) => {
          if (!started) { setTimeout(done, est * 1000); return; }
          // the words follow the real take (Shao's welcome runs ~20 s), from where the take starts
          const d = audio.voDuration?.(line.file);
          if (d) { durS = d + (performance.now() - t0) / 1000; clearTimeout(safety); safety = setTimeout(done, (durS + 4) * 1000); }
        })
        .catch(() => setTimeout(done, est * 1000));
      if (line.pre) {
        // the name first (a short live clip), then the canned tail
        let next = false;
        const go = () => { if (!next) { next = true; main(); } };
        audio.voFile(line.pre, { gain: 1.05, interrupt: true, duck: 0.08, onEnd: () => setTimeout(go, 120) })
          .then((started) => { if (!started) go(); }).catch(go);
        setTimeout(go, 2500);
      } else main();
      safety = setTimeout(done, (est + 6) * 1000);   // never wait forever on a stuck decode
    });
  }

  function close() {
    if (S.closed) return;
    S.closed = true; forge = null;
    cancelAnimationFrame(S.raf);
    removeEventListener('resize', onResize);
    window.visualViewport?.removeEventListener('resize', onResize);
    window.visualViewport?.removeEventListener('scroll', onResize);
    fadeRemove(root);
  }
  S.close = close;

  const frame = (now) => {
    if (S.closed) return;
    const raw = now - S.last;
    const dt = Math.min(0.05, raw / 1000); S.last = now; S.t += dt;
    step(S, dt); draw(S);
    // only a device that has SHOWN it can't keep up (1.5 s averaging under ~38 fps, after warm-up) renders
    // the canvas at a lower resolution; everyone else gets the full picture
    if (S.t > 1.2 && raw < 250) {
      S.ftSum = (S.ftSum || 0) + raw; S.ftN = (S.ftN || 0) + 1;
      if (S.ftSum > 1500) {
        if (S.ftSum / S.ftN > 26 && S.dprCap > 1.3 && (window.devicePixelRatio || 1) > 1.3) { S.dprCap = Math.max(1.25, S.dpr * 0.72); layout(); }
        S.ftSum = 0; S.ftN = 0;
      }
    }
    // the controls ride just under the lowest line of letters (one line or two)
    const low = S.glyphs.reduce((m, g) => Math.max(m, g.ty || 0), lettersY(S)) + S.fs * 0.32 + 18;
    if (Math.abs(low - (S.uiTop || 0)) > 2) { S.uiTop = low; ui.style.top = `${Math.round(low)}px`; }
    S.raf = requestAnimationFrame(frame);
  };
  S.raf = requestAnimationFrame(frame);
  // QA hooks (presentation only)
  window.__nmNameForge = { type: (v) => { input.value = v; input.dispatchEvent(new Event('input')); }, confirm: () => confirm(), state: () => ({ text: S.text, phase: S.phase, glyphs: S.glyphs.length, fs: S.fs }) };
}

// ---------------------------------------------------------------- forge simulation
// v299 (Jack: "not nearly epic enough ... and the frame rate is poor"): every letter is painted ONCE into
// offscreen sprites (cold forged gold, white-hot, a white mask for flashes and the glint), so a frame is
// only drawImage calls. The old renderer extruded every letter with ~30 fillTexts plus a blurred stroke
// on EVERY frame, which is what dropped the frame rate. Letters drop in red-hot, slam, squash, kick their
// neighbours, throw sparks that bounce on a reflective floor, and cool to gold; the confirm heats the
// whole name white, pushes the camera in and lands one colossal forge strike (flash, light rays, floor
// cracks, a double-image punch).
function fontOf(fs) { return `900 ${fs}px Unbounded, "Arial Black", sans-serif`; }
let mctx = null;
function measureCtx() { if (!mctx) mctx = document.createElement('canvas').getContext('2d'); return mctx; }
function w100(ch) { const c = measureCtx(); c.font = fontOf(100); return c.measureText(ch.toUpperCase()).width; }
const depthOf = (fs) => Math.max(5, fs * 0.15);
// A long name on a tall screen breaks at the space nearest its middle, so the letters stay GIANT.
function lineSplit(S) {
  const t = S.text;
  if (!(S.W < S.vh * 1.1) || t.trim().length < 8) return -1;
  let best = -1;
  for (let i = 1; i < t.length - 1; i++) if (t[i] === ' ' && (best < 0 || Math.abs(i - t.length / 2) < Math.abs(best - t.length / 2))) best = i;
  return best;
}
function fitFont(S) {
  const ctx = measureCtx(); ctx.font = fontOf(100);
  const t = (S.text || 'NAME').toUpperCase();
  const cut = lineSplit(S);
  const lines = cut > 0 ? [t.slice(0, cut), t.slice(cut + 1)] : [t];
  const w = Math.max(...lines.map(l => ctx.measureText(l).width), ctx.measureText('NAME').width * 0.8);
  const maxW = S.W * (S.W < 500 ? 0.88 : 0.82);
  const maxH = Math.min(S.vh * (lines.length > 1 ? 0.42 : 0.34), lines.length > 1 ? 420 : 340);
  S.lines = lines.length;
  S.fsTarget = Math.max(28, Math.min(maxH / (lines.length > 1 ? 2.1 : 1), (maxW / w) * 100));
  if (!S.fsInit) { S.fs = S.fsTarget; S.fsInit = true; }
}
function targets(S) {
  const chars = S.glyphs.filter(g => g.alive);
  const cut = lineSplit(S);
  const k = S.fs / 100, track = -0.02 * S.fs;
  const rows = cut > 0 ? [chars.slice(0, cut), chars.slice(cut + 1)] : [chars];
  if (cut > 0 && chars[cut]) { chars[cut].tx = S.W / 2; chars[cut].ty = lettersY(S); chars[cut].w = 0; chars[cut].bottom = false; }
  const lh = S.fs * 1.05;
  rows.forEach((row, r) => {
    const widths = row.map(g => g.w100 * k);
    const total = widths.reduce((a2, b2) => a2 + b2, 0) + track * Math.max(0, row.length - 1);
    let x = S.W / 2 - total / 2;
    const ty = lettersY(S) + (rows.length > 1 ? (r - 0.5) * lh + S.fs * 0.36 : 0);
    row.forEach((g, i) => { g.w = widths[i]; g.tx = x + widths[i] / 2; g.ty = ty; g.bottom = r === rows.length - 1; x += widths[i] + track; });
  });
}
function setText(S, v, rebuild = false) {
  const old = S.text;
  if (v === old && !rebuild) return;
  let common = 0;
  if (!rebuild) while (common < old.length && common < v.length && old[common] === v[common]) common++;
  const alive = S.glyphs.filter(g => g.alive);
  // removed glyphs shatter
  for (let i = alive.length - 1; i >= common; i--) { shatter(S, alive[i]); alive[i].alive = false; }
  if (alive.length > common) sfxDelete();
  S.glyphs = S.glyphs.filter(g => g.alive);
  // new glyphs drop in (staggered when several arrive at once: a paste or a suggestion)
  const add = v.slice(common);
  S.text = v;
  fitFont(S);
  [...add].forEach((ch, k) => {
    S.glyphs.push({ ch, alive: true, born: S.t + k * 0.07, landed: false, x: 0, y: 0, tx: 0, w: 0, w100: w100(ch), heat: 1, flash: 0,
      sq: 0, sqv: 0, ox: 0, oxv: 0, seed: Math.random() * 1000, bottom: true });
  });
  targets(S);
  for (const g of S.glyphs) { if (!g.x) g.x = g.tx; if (!g.y) g.y = g.ty; }
  // the stamp sounds fire as each glyph lands (step)
}
function lettersY(S) { return S.top + S.vh * (S.vh < 420 ? 0.36 : 0.40); }
const floorOf = (S) => (S.glyphs.length ? Math.max(...S.glyphs.map(g => g.ty || 0)) : lettersY(S)) + depthOf(S.fs);

// ---- sprites (built once per letter per size step; a frame only blits them)
let SPR = new Map(), SPR_OLD = new Map();
let sprF = 0, sprBuilt = 0;
// the sprite size only moves on when the letters have changed size by >15% (each glyph scales its sprite
// to the live size), and at most three letters are re-cut per frame -- the rest keep their old sprite
function sprSize(fs) {
  if (!sprF || Math.abs(fs - sprF) / sprF > 0.15) { SPR_OLD = SPR; SPR = new Map(); sprF = Math.max(24, Math.round(fs / 10) * 10); }
  sprBuilt = 0;
  return sprF;
}
function mkLayer(W, H, r, F) {
  const c = document.createElement('canvas'); c.width = Math.max(1, Math.ceil(W * r)); c.height = Math.max(1, Math.ceil(H * r));
  const x = c.getContext('2d'); x.scale(r, r); x.font = fontOf(F); x.textAlign = 'center'; x.textBaseline = 'alphabetic'; x.lineJoin = 'round';
  return [c, x];
}
function glyphSprite(ch, F, dpr) {
  const key = ch.toUpperCase();
  let s = SPR.get(key);
  if (s) return s;
  if (sprBuilt >= 3 && SPR_OLD.has(key)) return SPR_OLD.get(key);
  sprBuilt++;
  const C = key, w = Math.max(F * 0.2, w100(C) * F / 100), D = depthOf(F);
  const padX = F * 0.08, W = w + padX * 2 + D * 0.4, top = F * 0.92, H = top + D + F * 0.1, ax = padX + w / 2 + D * 0.04, ay = top;
  const sd = Math.min(2, dpr);
  const body = (x, hot) => {
    // the block's depth: a stack of silhouettes, near-black at the back (red-hot when hot)
    const n = Math.max(6, Math.min(24, Math.round(D / 1.3)));
    for (let i = n; i >= 1; i--) {
      const d = D * i / n, t = i / n;
      x.fillStyle = hot ? `rgb(${Math.round(255 - 120 * t)},${Math.round(120 - 105 * t)},${Math.round(30 - 26 * t)})`
        : `rgb(${Math.round(132 - 112 * t)},${Math.round(78 - 68 * t)},${Math.round(26 - 22 * t)})`;
      x.fillText(C, ax + d * 0.3, ay + d);
    }
    x.lineWidth = F * 0.075; x.strokeStyle = hot ? '#6a1800' : '#160a02'; x.strokeText(C, ax, ay);
    const rim = x.createLinearGradient(0, ay - F * 0.8, 0, ay);
    if (hot) { rim.addColorStop(0, '#ffffff'); rim.addColorStop(1, '#ffc35e'); }
    else { rim.addColorStop(0, '#fff7d6'); rim.addColorStop(0.5, '#a9661a'); rim.addColorStop(1, '#ffe596'); }
    x.lineWidth = F * 0.034; x.strokeStyle = rim; x.strokeText(C, ax, ay);
  };
  const face = (r, hot) => {
    const [c, x] = mkLayer(W, H, r, F);
    const g = x.createLinearGradient(0, ay - F * 0.8, 0, ay);
    const stops = hot
      ? [[0, '#ffffff'], [0.4, '#fffbe2'], [0.62, '#ffd56c'], [0.86, '#ff8d22'], [1, '#ffd88a']]
      : [[0, '#fffdf2'], [0.22, '#ffe8a6'], [0.45, '#f1b23e'], [0.5, '#6f3906'], [0.57, '#b56a14'], [0.8, '#f7c654'], [1, '#fff4c8']];
    for (const [o, col] of stops) g.addColorStop(o, col);
    x.fillStyle = g; x.fillText(C, ax, ay);
    x.globalCompositeOperation = 'source-atop';
    // bevel as inner shadows (a stroke would trace the font's overlapping inner contours): a sheet with a
    // letter-shaped hole casts light down onto the top edges and shade up onto the bottom ones
    const [hc, hx] = mkLayer(W, H, r, F);
    hx.fillStyle = '#000'; hx.fillRect(0, 0, W, H); hx.globalCompositeOperation = 'destination-out'; hx.fillText(C, ax, ay);
    x.save(); x.setTransform(1, 0, 0, 1, 0, 0);
    for (const [col, oy, bl] of [['rgba(255,255,255,0.8)', 0.03, 0.035], [hot ? 'rgba(170,50,0,0.55)' : 'rgba(70,26,0,0.6)', -0.03, 0.04]]) {
      x.shadowColor = col; x.shadowOffsetX = 0; x.shadowOffsetY = F * oy * r; x.shadowBlur = F * bl * r;
      x.drawImage(hc, 0, 0);
    }
    x.restore();
    if (!hot) {
      // brushed metal and one soft specular bloom
      for (let yy = ay - F * 0.82; yy < ay; yy += 2) { x.fillStyle = `rgba(${Math.random() < 0.5 ? '255,255,255' : '60,30,0'},${0.01 + Math.random() * 0.025})`; x.fillRect(0, yy, W, 1); }
      const sp = x.createRadialGradient(ax - w * 0.22, ay - F * 0.62, 0, ax - w * 0.22, ay - F * 0.62, F * 0.4);
      sp.addColorStop(0, 'rgba(255,255,255,0.45)'); sp.addColorStop(1, 'rgba(255,255,255,0)');
      x.fillStyle = sp; x.fillRect(0, 0, W, H);
    }
    return c;
  };
  const [cold, cx] = mkLayer(W, H, sd, F); body(cx, false); cx.drawImage(face(sd, false), 0, 0, W, H);
  const [hot, hx] = mkLayer(W, H, 1, F); body(hx, true); hx.drawImage(face(1, true), 0, 0, W, H);
  const [mask, mx] = mkLayer(W, H, 1, F); mx.fillStyle = '#fff'; mx.fillText(C, ax, ay);
  s = { cold, hot, mask, W, H, ax, ay, F };
  SPR.set(key, s);
  return s;
}
function softSprite(size, stops) {
  const c = document.createElement('canvas'); c.width = c.height = size;
  const x = c.getContext('2d'), g = x.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  for (const [o, col] of stops) g.addColorStop(o, col);
  x.fillStyle = g; x.fillRect(0, 0, size, size);
  return c;
}
let FX = null;
function fxSprites() {
  if (FX) return FX;
  const rays = document.createElement('canvas'); rays.width = rays.height = 512;
  const rx = rays.getContext('2d');
  const rg = rx.createRadialGradient(256, 256, 0, 256, 256, 256);
  rg.addColorStop(0, 'rgba(255,236,190,0.95)'); rg.addColorStop(0.35, 'rgba(255,180,80,0.45)'); rg.addColorStop(1, 'rgba(255,140,40,0)');
  rx.fillStyle = rg; rx.translate(256, 256);
  for (let i = 0; i < 22; i++) {
    const a = (i / 22) * Math.PI * 2 + Math.sin(i * 7.3) * 0.08, wd = 0.025 + Math.abs(Math.sin(i * 3.1)) * 0.06;
    rx.beginPath(); rx.moveTo(0, 0); rx.arc(0, 0, 256, a - wd, a + wd); rx.closePath(); rx.fill();
  }
  FX = {
    glow: softSprite(128, [[0, 'rgba(255,190,90,1)'], [0.4, 'rgba(255,130,40,0.45)'], [1, 'rgba(255,90,20,0)']]),
    dot: softSprite(32, [[0, 'rgba(255,250,220,1)'], [0.3, 'rgba(255,190,90,0.8)'], [1, 'rgba(255,120,30,0)']]),
    smoke: softSprite(96, [[0, 'rgba(150,130,125,0.55)'], [0.6, 'rgba(90,75,80,0.2)'], [1, 'rgba(60,50,60,0)']]),
    rays,
  };
  return FX;
}
// the static backdrop and the floor, rebuilt only on resize (the floor is one strip blitted at its height)
function buildBackdrop(S) {
  const cw = S.canvas.width, ch = S.canvas.height, r = S.dpr;
  const bg = document.createElement('canvas'); bg.width = cw; bg.height = ch;
  const x = bg.getContext('2d'); x.scale(r, r);
  const y = lettersY(S), W = S.W, H = S.H;
  const g = x.createRadialGradient(W / 2, y - S.fs * 0.2, 10, W / 2, y, Math.max(W, H) * 0.9);
  g.addColorStop(0, '#3b1f18'); g.addColorStop(0.32, '#1c1020'); g.addColorStop(0.7, '#0b0712'); g.addColorStop(1, '#030206');
  x.fillStyle = g; x.fillRect(0, 0, W, H);
  // faint smoke banks in the dark
  for (let i = 0; i < 7; i++) {
    const sx = W * (0.1 + 0.8 * Math.random()), sy = H * (0.1 + 0.5 * Math.random()), sr = Math.max(W, H) * (0.18 + Math.random() * 0.2);
    const sg = x.createRadialGradient(sx, sy, 0, sx, sy, sr);
    sg.addColorStop(0, 'rgba(120,80,120,0.07)'); sg.addColorStop(1, 'rgba(120,80,120,0)');
    x.fillStyle = sg; x.fillRect(0, 0, W, H);
  }
  // the floor: obsidian with a gold horizon line and perspective grooves
  const FH = Math.ceil(H * 0.75);
  const fl = document.createElement('canvas'); fl.width = cw; fl.height = Math.ceil(FH * r);
  const f = fl.getContext('2d'); f.scale(r, r);
  const fg = f.createLinearGradient(0, 0, 0, FH);
  fg.addColorStop(0, 'rgba(20,10,8,0.92)'); fg.addColorStop(0.5, 'rgba(8,5,9,0.97)'); fg.addColorStop(1, 'rgba(3,2,5,1)');
  f.fillStyle = fg; f.fillRect(0, 0, W, FH);
  // worn stone, not a grid: a few soft scorch pools in the dark
  for (let i = 0; i < 9; i++) {
    const px = W * Math.random(), py = FH * (0.1 + 0.8 * Math.random()), pr = W * (0.08 + Math.random() * 0.16);
    const pg = f.createRadialGradient(px, py, 0, px, py, pr);
    pg.addColorStop(0, `rgba(${Math.random() < 0.5 ? '0,0,0,0.35' : '90,50,30,0.10'})`); pg.addColorStop(1, 'rgba(0,0,0,0)');
    f.save(); f.scale(1, 0.35); f.fillStyle = pg; f.fillRect(0, 0, W, FH / 0.35); f.restore();
  }
  const hz = f.createLinearGradient(0, 0, W, 0);
  hz.addColorStop(0, 'rgba(255,190,90,0)'); hz.addColorStop(0.5, 'rgba(255,214,140,0.55)'); hz.addColorStop(1, 'rgba(255,190,90,0)');
  f.fillStyle = hz; f.fillRect(0, 0, W, 2);
  const vg = f.createLinearGradient(0, 0, 0, FH * 0.3);
  vg.addColorStop(0, 'rgba(255,170,70,0.10)'); vg.addColorStop(1, 'rgba(255,170,70,0)');
  f.fillStyle = vg; f.fillRect(0, 0, W, FH * 0.3);
  S.bg = bg; S.floor = fl; S.floorH = FH;
}

function newEmber(anywhere) {
  return { x: Math.random(), y: anywhere ? Math.random() : 1.05, vy: 0.02 + Math.random() * 0.07, r: 0.8 + Math.random() * 2.2,
    a: 0.2 + Math.random() * 0.6, sway: Math.random() * 6.28 };
}
function sparks(S, x, y, n, power, up = 0.6) {
  if (REDUCED) n = Math.ceil(n / 4);
  n = Math.min(n, 900 - S.parts.length);
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * (2 - up), sp = power * (0.3 + Math.random() * 0.9);
    S.parts.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 0, max: 0.5 + Math.random() * 0.9, b: 0 });
  }
}
function smoke(S, x, y, n, size) {
  if (REDUCED) n = Math.ceil(n / 3);
  for (let i = 0; i < n; i++) S.smokes.push({ x: x + (Math.random() - 0.5) * size, y, vx: (Math.random() - 0.5) * size * 1.6, vy: -20 - Math.random() * 40,
    s: size * (0.5 + Math.random() * 0.6), life: 0, max: 0.9 + Math.random() * 0.8 });
}
function shatter(S, g) {
  const y = g.y || lettersY(S), fs = S.fs;
  const n = REDUCED ? 4 : 18;
  for (let i = 0; i < n; i++) {
    S.shards.push({ x: g.x + (Math.random() - 0.5) * g.w, y: y - fs * 0.35 + (Math.random() - 0.3) * fs * 0.7,
      vx: (Math.random() - 0.5) * 520, vy: -160 - Math.random() * 420, rot: Math.random() * 6.28, vr: (Math.random() - 0.5) * 16,
      s: fs * (0.06 + Math.random() * 0.11), life: 0, max: 0.9 + Math.random() * 0.6 });
  }
  sparks(S, g.x, y - fs * 0.35, 18, 380, 1.6);
  smoke(S, g.x, y - fs * 0.2, 3, fs * 0.5);
}
function shatterAll(S) {
  for (const g of S.glyphs) if (g.alive) { shatter(S, g); g.alive = false; }
  S.glyphs = []; S.text = '';
  sfxDelete();
}
function nope(S) { S.nope = 1; sfxNope(); }
// one letter lands: the strike everything else reacts to
function land(S, g) {
  g.landed = true; g.flash = 1; g.heat = 1; g.sq = -0.3; g.sqv = 0;
  const i = S.glyphs.indexOf(g);
  const quick = S.t - (S.lastLand || -9) < 0.32;
  S.combo = quick ? Math.min(8, (S.combo || 0) + 1) : 0; S.lastLand = S.t;
  for (const [j, dir] of [[i - 1, -1], [i + 1, 1]]) { const n = S.glyphs[j]; if (n && n.landed) { n.oxv += dir * S.fs * 2.2; n.sqv -= 1.2; } }
  sfxKey(i);
  if (!REDUCED) { S.shake = Math.max(S.shake, 0.5 + S.combo * 0.05); S.punch = Math.max(S.punch, 0.016 + S.combo * 0.002); }
  S.bgHeat = Math.min(1, S.bgHeat + 0.22 + S.combo * 0.03);
  const fy = (g.ty ?? lettersY(S)) + depthOf(S.fs);
  sparks(S, g.x, fy - S.fs * 0.05, 34 + S.combo * 8, 380 + S.fs * 1.2, 0.9);
  smoke(S, g.x, fy, 2, S.fs * 0.55);
  S.rings.push({ x: g.x, y: fy, r: g.w * 0.4, life: 0, max: 0.6, w: 4, grow: S.W * 0.55 });
}
// the confirm: one colossal strike on the whole name
function slam(S) {
  S.flash = 1; S.impactT = S.t; S.echo = 1; S.charging = false;
  if (!REDUCED) { S.shake = Math.max(S.shake, 1.7); S.punch = 0.085; }
  S.raysOn = true; S.bgHeat = 1;
  const fy = floorOf(S), cx = S.W / 2;
  S.rings.push({ x: cx, y: fy, r: 20, life: 0, max: 1.3, w: 12, grow: S.W * 1.4 });
  S.rings.push({ x: cx, y: fy, r: 10, life: 0, max: 0.9, w: 6, grow: S.W * 0.8 });
  for (const g of S.glyphs) {
    g.flash = 1; g.heat = 1; g.sq = -0.4; g.sqv = 0;
    sparks(S, g.x, fy - S.fs * 0.1, 60, 700 + S.fs * 1.5, 1.1);
    smoke(S, g.x, fy, 3, S.fs * 0.8);
  }
  S.cracks = makeCracks(S, cx, fy);
}
function makeCracks(S, cx, fy) {
  if (REDUCED) return null;
  const c = document.createElement('canvas'); c.width = S.canvas.width; c.height = S.canvas.height;
  const x = c.getContext('2d'); x.scale(S.dpr, S.dpr);
  x.lineCap = 'round'; x.lineJoin = 'round';
  const paths = [];
  for (let i = 0; i < 13; i++) {
    const a = (i / 13) * Math.PI * 2 + Math.random() * 0.3, len = S.W * (0.18 + Math.random() * 0.32);
    let px = cx + Math.cos(a) * S.fs * 0.3, py = fy + Math.sin(a) * S.fs * 0.06;
    const pts = [[px, py]];
    for (let k = 1; k <= 7; k++) {
      const ja = a + (Math.random() - 0.5) * 0.7;
      px += Math.cos(ja) * len / 7; py += Math.sin(ja) * len / 7 * 0.26;
      if (py < fy) py = fy + Math.random() * 3;
      pts.push([px, py]);
    }
    paths.push(pts);
  }
  for (const [lw, col] of [[7, 'rgba(255,110,20,0.35)'], [3, 'rgba(255,170,60,0.9)'], [1.2, 'rgba(255,250,220,1)']]) {
    x.lineWidth = lw; x.strokeStyle = col;
    for (const pts of paths) { x.beginPath(); pts.forEach(([a2, b2], k) => (k ? x.lineTo(a2, b2) : x.moveTo(a2, b2))); x.stroke(); }
  }
  return c;
}
// while Shao is still recording: another blow on the glowing name (each a little different)
function aftershock(S) {
  if (S.closed || !S.glyphs.length) return;
  S.blows = (S.blows || 0) + 1;
  const g = S.glyphs[(S.blows * 3) % S.glyphs.length], fy = floorOf(S);
  for (const [j, o] of S.glyphs.entries()) { o.heat = Math.max(o.heat, 0.85); o.sq = -0.22 * (o === g ? 1.4 : 0.6); o.flash = o === g ? 1 : 0.35; o.oxv += (j - S.glyphs.indexOf(g)) * S.fs * 0.6; }
  if (!REDUCED) { S.shake = Math.max(S.shake, 0.8); S.punch = Math.max(S.punch, 0.03); }
  S.echo = 0.6; S.bgHeat = 1;
  sparks(S, g.x, fy - S.fs * 0.1, 70, 600 + S.fs * 1.4, 1);
  smoke(S, g.x, fy, 3, S.fs * 0.6);
  S.rings.push({ x: g.x, y: fy, r: g.w * 0.5, life: 0, max: 0.8, w: 7, grow: S.W * 0.9 });
  sfxKey(S.blows % 8);
}
function flyOut(S) { S.leaving = S.t; }

function spring(v, u, k, c, dt) { u += (-k * v - c * u) * dt; return [v + u * dt, u]; }
function step(S, dt) {
  const y = lettersY(S);
  S.fs += (S.fsTarget - S.fs) * Math.min(1, dt * 10);
  targets(S);
  const chargeP = S.charging ? Math.min(1, (S.t - S.chargeT) / 0.75) : 0;
  S.glyphs.forEach((g, i) => {
    g.x += (g.tx - g.x) * Math.min(1, dt * 14);
    g.y += ((g.ty ?? y) - g.y) * Math.min(1, dt * 14);
    if (!g.landed && S.t - g.born >= 0.12) land(S, g);
    // springs: the landing squash and the knock from a neighbour (sub-stepped: stiff springs at 30 fps)
    for (let k = 0; k < 2; k++) { [g.sq, g.sqv] = spring(g.sq, g.sqv, 420, 15, dt / 2); [g.ox, g.oxv] = spring(g.ox, g.oxv, 300, 13, dt / 2); }
    if (S.charging) g.heat = Math.max(g.heat, Math.min(1, chargeP * 1.6 - i * 0.05));
    else g.heat = Math.max(0, g.heat - dt * (S.impactT != null ? 0.45 : 0.95));
    g.flash = Math.max(0, g.flash - dt * 3.4);
  });
  S.shake = Math.max(0, S.shake - dt * 3.2);
  S.punch = Math.max(0, S.punch - dt * (S.punch > 0.03 ? 0.25 : 0.08));
  S.push = S.charging ? chargeP * 0.035 : Math.max(0, (S.push || 0) - dt * 0.12);
  S.flash = Math.max(0, S.flash - dt * 1.7);
  S.echo = Math.max(0, (S.echo || 0) - dt * 2.6);
  S.flinch = Math.max(0, S.flinch - dt * 5);
  S.nope = Math.max(0, (S.nope || 0) - dt * 2.2);
  S.bgHeat = S.charging ? Math.min(1, S.bgHeat + dt * 1.5) : Math.max(S.raysOn ? 0.45 : 0, S.bgHeat - dt * 0.6);
  const raysT = S.raysOn ? (S.t - S.impactT < 0.25 ? 1 : 0.5) : S.charging ? chargeP * 0.25 : 0;
  S.rays += (raysT - S.rays) * Math.min(1, dt * (S.rays < raysT ? 9 : 1.5));
  S.crackA = S.cracks ? Math.max(0, 1 - Math.max(0, S.t - S.impactT - 0.4) / 2.2) : 0;
  if (S.cracks && S.crackA <= 0) S.cracks = null;
  const fy = floorOf(S);
  for (const p of S.parts) {
    p.life += dt; p.vy += 1100 * dt; p.vx *= 1 - dt * 1.2; p.x += p.vx * dt; p.y += p.vy * dt;
    if (p.y > fy && p.vy > 0 && p.b < 2) { p.y = fy; p.vy *= -0.32; p.vx *= 0.75; p.b++; }
  }
  S.parts = S.parts.filter(p => p.life < p.max);
  for (const s of S.shards) {
    s.life += dt; s.vy += 1400 * dt; s.x += s.vx * dt; s.y += s.vy * dt; s.rot += s.vr * dt;
    if (s.y > fy && s.vy > 0) { s.y = fy; s.vy *= -0.3; s.vx *= 0.6; s.vr *= 0.5; }
  }
  S.shards = S.shards.filter(s => s.life < s.max);
  for (const m of S.smokes) { m.life += dt; m.x += m.vx * dt; m.y += m.vy * dt; m.vx *= 1 - dt * 1.5; m.s += dt * m.s * 0.6; }
  S.smokes = S.smokes.filter(m => m.life < m.max);
  for (const r of S.rings) { r.life += dt; r.r += r.grow * dt * (1 - r.life / r.max); }
  S.rings = S.rings.filter(r => r.life < r.max);
  const lift = 1 + S.bgHeat * 2 + chargeP * 5;
  for (const e of S.embers) {
    e.y -= e.vy * dt * lift; e.sway += dt;
    if (chargeP) e.x += (0.5 - e.x) * dt * chargeP * 0.8;
    if (e.y < -0.05) Object.assign(e, newEmber(false));
  }
}

function draw(S) {
  const { ctx, W, H, dpr } = S;
  const fx = fxSprites();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(S.bg, 0, 0, W, H);
  const y = lettersY(S), fs = S.fs, fy = floorOf(S), cx = W / 2;
  const nameY = y - fs * (S.lines > 1 ? 0 : 0.35);
  ctx.save();
  // camera: the charge pushes in, every strike punches, the shake rattles
  const z = 1 + (S.push || 0) + S.punch;
  if (z !== 1) { ctx.translate(cx, nameY); ctx.scale(z, z); ctx.translate(-cx, -nameY); }
  if (S.shake > 0) ctx.translate((Math.random() - 0.5) * S.shake * 16, (Math.random() - 0.5) * S.shake * 12);
  if (S.nope > 0) ctx.translate(Math.sin(S.t * 70) * S.nope * 16, 0);
  // heat behind the name, light rays after the strike (both behind the floor)
  ctx.globalCompositeOperation = 'lighter';
  const heatA = 0.22 + S.bgHeat * 0.4 + S.flash * 0.5;
  ctx.globalAlpha = Math.min(1, heatA);
  const gw = Math.max(W * 0.7, fs * 6), gh = Math.max(fs * 3, H * 0.45);
  ctx.drawImage(fx.glow, cx - gw / 2, nameY - gh / 2, gw, gh);
  if (S.rays > 0.01) {
    const rs = Math.max(W, H) * (1.1 + 0.25 * Math.min(1, (S.t - (S.impactT ?? S.t)) * 3));
    ctx.save(); ctx.translate(cx, nameY); ctx.rotate(S.t * 0.12); ctx.globalAlpha = Math.min(1, S.rays);
    ctx.drawImage(fx.rays, -rs / 2, -rs / 2, rs, rs);
    ctx.rotate(-S.t * 0.27); ctx.globalAlpha = Math.min(1, S.rays) * 0.5;
    ctx.drawImage(fx.rays, -rs * 0.4, -rs * 0.4, rs * 0.8, rs * 0.8);
    ctx.restore();
  }
  ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1;
  ctx.drawImage(S.floor, -W * 0.1, fy, W * 1.2, S.floorH);
  ctx.globalCompositeOperation = 'lighter';
  if (S.cracks && S.crackA > 0) { ctx.globalAlpha = S.crackA; ctx.drawImage(S.cracks, 0, 0, W, H); }
  for (const e of S.embers) {
    const ex = e.x * W + Math.sin(e.sway) * 9, ey = e.y * H, r = e.r * 3;
    ctx.globalAlpha = e.a * (0.6 + S.bgHeat * 0.4);
    ctx.drawImage(fx.dot, ex - r, ey - r, r * 2, r * 2);
  }
  ctx.globalCompositeOperation = 'source-over';
  // smoke puffs (behind the letters)
  for (const m of S.smokes) {
    const a = Math.sin(Math.PI * Math.min(1, m.life / m.max));
    ctx.globalAlpha = a * 0.7;
    ctx.drawImage(fx.smoke, m.x - m.s, m.y - m.s * 0.7, m.s * 2, m.s * 1.4);
  }
  ctx.globalAlpha = 1;

  const leaving = S.leaving != null ? Math.min(1, (S.t - S.leaving) / 0.45) : 0;
  const F = sprSize(S.fsTarget);
  const chargeP = S.charging ? Math.min(1, (S.t - S.chargeT) / 0.75) : 0;
  // per-glyph placement, shared by the reflection pass and the letter pass
  const placed = [];
  S.glyphs.forEach((g, i) => {
    const age = S.t - g.born;
    if (age < 0) return;
    const k = Math.min(1, age / 0.12);
    const drop = REDUCED || g.landed ? 0 : -fs * 1.4 * (1 - k * k);
    const spr = glyphSprite(g.ch, F, dpr);
    const sc = (g.landed ? 1 : 1 + 0.18 * (1 - k)) * (fs / spr.F);
    const bob = REDUCED ? 0 : Math.sin(S.t * 2.1 + i * 0.7) * fs * 0.01;
    const jit = chargeP ? (Math.random() - 0.5) * fs * 0.03 * chargeP : 0;
    const rise = -chargeP * fs * 0.08;
    const flinchR = S.flinch * Math.sin(g.seed + S.t * 40) * 0.07;
    const sagR = S.sag * (Math.sin(g.seed) * 0.2), sagY = S.sag * fs * (0.04 + 0.07 * Math.abs(Math.sin(g.seed * 3)));
    const gy = (g.y || y) + bob + drop + sagY + rise - leaving * fs * 1.8 * (1 + i * 0.12);
    placed.push({ g, i, k, gx: g.x + g.ox + jit, gy, sc, rot: sagR + flinchR, sx: 1 - g.sq * 0.55, sy: 1 + g.sq,
      alpha: Math.min(1, age / 0.05) * (1 - leaving), spr });
  });
  // the polished floor's reflection
  if (!REDUCED) for (const p of placed) {
    if (!p.g.bottom || !p.g.landed) continue;
    const s = p.spr, gfy = (p.g.ty ?? y) + depthOf(fs);
    ctx.save(); ctx.translate(p.gx, gfy); ctx.scale(p.sc * p.sx, -p.sc * 0.62);
    ctx.globalAlpha = p.alpha * 0.18;
    ctx.drawImage(s.cold, -s.ax, -s.ay - depthOf(s.F), s.W, s.H);
    if (p.g.heat > 0.05) { ctx.globalAlpha = p.alpha * 0.18 * p.g.heat; ctx.drawImage(s.hot, -s.ax, -s.ay - depthOf(s.F), s.W, s.H); }
    ctx.restore();
  }
  // hot letters light the air around them
  ctx.globalCompositeOperation = 'lighter';
  for (const p of placed) {
    const h = p.g.heat * 0.8 + p.g.flash * 0.5 + 0.08;
    if (h < 0.02) continue;
    ctx.globalAlpha = Math.min(1, h) * p.alpha;
    const gs = fs * 1.9;
    ctx.drawImage(fx.glow, p.gx - gs / 2, p.gy - fs * 0.4 - gs / 2, gs, gs);
  }
  ctx.globalCompositeOperation = 'source-over';
  for (const p of placed) {
    const { g, spr: s } = p;
    ctx.save();
    ctx.translate(p.gx, p.gy);
    ctx.rotate(p.rot);
    ctx.scale(p.sc * p.sx, p.sc * p.sy);   // squash pivots on the baseline: the letter hits the floor
    const dx = -s.ax, dy = -s.ay;
    if (!g.landed && !REDUCED) {
      // motion streak while it falls
      ctx.globalAlpha = p.alpha * 0.22; ctx.drawImage(s.hot, dx, dy - fs * 0.5 / p.sc, s.W, s.H);
      ctx.globalAlpha = p.alpha * 0.1; ctx.drawImage(s.hot, dx, dy - fs * 0.95 / p.sc, s.W, s.H);
    }
    ctx.globalAlpha = p.alpha;
    ctx.drawImage(s.cold, dx, dy, s.W, s.H);
    if (g.heat > 0.01) { ctx.globalAlpha = p.alpha * Math.min(1, g.heat); ctx.drawImage(s.hot, dx, dy, s.W, s.H); }
    ctx.globalCompositeOperation = 'lighter';
    if (g.flash > 0.01) { ctx.globalAlpha = p.alpha * g.flash; ctx.drawImage(s.mask, dx, dy, s.W, s.H); }
    if (S.echo > 0.01) {
      // the strike's double image
      ctx.globalAlpha = p.alpha * S.echo * 0.45;
      const o = (1 - S.echo) * fs * 0.25 / p.sc + 4;
      ctx.drawImage(s.cold, dx - o, dy, s.W, s.H); ctx.drawImage(s.cold, dx + o, dy, s.W, s.H);
    }
    // a glint sweeping across the name
    const sweep = ((S.t * 0.42 - p.i * 0.05) % 1.8) - 0.3;
    if (sweep > -0.2 && sweep < 1.2) {
      const bx = dx + s.W * sweep;
      ctx.beginPath(); ctx.moveTo(bx, dy - s.H); ctx.lineTo(bx + s.F * 0.16, dy - s.H); ctx.lineTo(bx - s.F * 0.2, dy + s.H); ctx.lineTo(bx - s.F * 0.36, dy + s.H); ctx.closePath();
      ctx.clip();
      ctx.globalAlpha = p.alpha * 0.32;
      ctx.drawImage(s.mask, dx, dy, s.W, s.H);
    }
    ctx.restore();
  }
  // the caret: a molten bar where the next letter lands
  if (S.phase === 'type' && !S.charging) {
    const last = [...placed].reverse().find(p => p.g.bottom !== false);
    const kx = last ? last.gx + last.g.w / 2 + fs * 0.14 : cx;
    const ky = last ? (last.g.ty ?? y) : y;
    const a = 0.45 + 0.4 * Math.sin(S.t * 6);
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = a * 0.7; ctx.drawImage(fx.glow, kx - fs * 0.3, ky - fs * 0.7, fs * 0.6, fs * 0.8);
    ctx.globalAlpha = a; ctx.fillStyle = '#ffd9a0';
    ctx.fillRect(kx - Math.max(2, fs * 0.012), ky - fs * 0.74, Math.max(4, fs * 0.024), fs * 0.76);
    ctx.globalCompositeOperation = 'source-over';
  }
  // shockwave rings on the floor
  ctx.globalCompositeOperation = 'lighter';
  for (const r of S.rings) {
    const a = 1 - r.life / r.max;
    ctx.globalAlpha = a; ctx.strokeStyle = 'rgb(255,200,120)'; ctx.lineWidth = r.w * a + 1;
    ctx.beginPath(); ctx.ellipse(r.x, r.y, r.r, r.r * 0.2, 0, 0, 6.283); ctx.stroke();
  }
  // sparks: three colour bands, one path each
  ctx.globalAlpha = 1; ctx.lineCap = 'round';
  for (const [lo, hi, col, lw] of [[0, 0.3, 'rgb(255,250,215)', 2.6], [0.3, 0.65, 'rgb(255,190,80)', 2], [0.65, 1.01, 'rgb(255,100,30)', 1.4]]) {
    ctx.strokeStyle = col; ctx.lineWidth = lw; ctx.beginPath();
    for (const p of S.parts) {
      const q = p.life / p.max;
      if (q < lo || q >= hi) continue;
      ctx.moveTo(p.x, p.y); ctx.lineTo(p.x - p.vx * 0.022, p.y - p.vy * 0.022);
    }
    ctx.stroke();
  }
  ctx.globalCompositeOperation = 'source-over';
  for (const s of S.shards) {
    const a = 1 - s.life / s.max;
    ctx.save(); ctx.translate(s.x, s.y); ctx.rotate(s.rot); ctx.globalAlpha = a;
    ctx.fillStyle = '#e9a73c';
    ctx.beginPath(); ctx.moveTo(0, -s.s); ctx.lineTo(s.s * 0.8, s.s * 0.6); ctx.lineTo(-s.s * 0.7, s.s * 0.5); ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#fff1c2';
    ctx.beginPath(); ctx.moveTo(0, -s.s); ctx.lineTo(s.s * 0.25, 0); ctx.lineTo(-s.s * 0.7, s.s * 0.5); ctx.closePath(); ctx.fill();
    ctx.restore();
  }
  ctx.restore();
  ctx.globalAlpha = 1;
  if (S.flash > 0) { ctx.fillStyle = `rgba(255,238,200,${S.flash * 0.6})`; ctx.fillRect(0, 0, W, H); }
}

// ======================================================================= PAGES (sign in, link)
// audiopolish: the server could not be reached (no response, a timeout or a 5xx with no reason of its
// own) -> a short styled line with a Retry, instead of a silent failure or a bare grey sentence.
function isNetErr(e) { return !e || !e.status || (e.status >= 500 && !(e.body && e.body.code)); }
function netErr(host, retry) {
  host.textContent = '';
  const box = el('div', 'acct-net'); box.setAttribute('role', 'alert');
  const b = el('button', null, 'Retry'); b.type = 'button';
  b.addEventListener('click', () => { host.textContent = ''; retry(); });
  box.append(el('span', null, 'Can\u2019t reach the server \u2014 try again'), b);
  host.append(box);
}
// audiopolish: account pages fade in (CSS above) and fade out instead of hard-cutting the screen beneath
function fadeRemove(node) {
  if (!node || node.classList.contains('acct-out')) return;
  node.classList.add('acct-out');
  setTimeout(() => node.remove(), 230);
}
function page(id) {
  injectStyle();
  let p = $(id);
  if (p) p.remove();
  p = el('div', 'acct-page open'); p.id = id; p.setAttribute('role', 'dialog');
  const wrap = el('div', 'acct-wrap'); p.appendChild(wrap);
  document.body.appendChild(p);
  return { p, wrap };
}
const fmtLeft = (ms) => { const s = Math.max(0, Math.round(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
function qrBox(svg) {
  const q = el('div', 'acct-qr' + (svg ? '' : ' empty'));
  if (svg && /^<svg[\s>]/.test(svg)) q.innerHTML = svg;   // server-rendered by the qrcode package (paths only)
  else q.textContent = '…';
  return q;
}
// polB: recorded cues only (assets/sfx2): a tab tick, and a success sting when a device gets in / is let in
const PAGE_CUES = ['tick', 'deckready', 'tradecomplete'];
function preloadCues() { audio.loadSfx(PAGE_CUES).catch(() => {}); }
function cue(name, gain) { try { audio.play(name, { gain }); } catch { /* audio is never in the way */ } }
// polB: a segmented control; each step slides in from the side it sits on. defs: [{ key, label, node }]
function stepper(defs, initial) {
  const bar = el('div', 'acct-tabs'); bar.setAttribute('role', 'tablist');
  let cur = null;
  const show = (key, quiet = false) => {
    if (key === cur) return;
    const from = defs.findIndex(d => d.key === cur), to = defs.findIndex(d => d.key === key);
    for (const d of defs) {
      const on = d.key === key;
      d.btn.classList.toggle('on', on); d.btn.setAttribute('aria-selected', String(on));
      d.node.classList.toggle('on', on); d.node.classList.toggle('back', on && from > to);
    }
    if (!quiet) cue('tick', 0.4);
    cur = key;
  };
  for (const d of defs) {
    d.node.classList.add('acct-step');
    d.btn = el('button', 'acct-tab', d.label); d.btn.type = 'button'; d.btn.setAttribute('role', 'tab');
    d.btn.addEventListener('click', () => show(d.key));
    bar.append(d.btn);
  }
  show(initial, true);
  return { bar, show };
}

/** A login link opened on a device that is already signed in: ask before switching accounts. */
export function confirmLoginSwitch(current) {
  return new Promise((resolve) => {
    const { p, wrap } = page('acctSwitch');
    const done = (v) => { fadeRemove(p); resolve(v); };
    const card = el('div', 'acct-card');
    card.append(el('h3', null, 'Switch accounts?'),
      el('p', null, `This link signs this device in as someone else. You are ${current}. Only switch if the link is yours.`));
    const stay = el('button', 'acct-btn', `Stay as ${current}`); stay.type = 'button';
    const go = el('button', 'acct-btn2', 'Switch'); go.type = 'button';
    stay.addEventListener('click', () => done(false));
    go.addEventListener('click', () => done(true));
    card.append(stay, go, el('p', 'acct-small', `Come back any time: Menu → Account → Switch player.`));
    wrap.style.justifyContent = 'center';
    wrap.append(card);
  });
}

/** "I already have an account": name + PIN, or link from another device, or ask Jack.
 *  P1 SWITCHING: every opening is one ATTEMPT (signInAttempt). Credentials are obtained without touching the session;
 *  the account is adopted only while this attempt is still current, so Back (or a newer sign-in page) drops any late
 *  PIN / stored-account / pairing answer instead of changing identity underneath the player.
 *  polB: two steps behind a segmented control -- SCAN (this device's code, the QR as the hero) and NAME + PIN. Both
 *  live in the DOM at once (the pairing poll runs whichever step is showing); Back always closes the page. */
let signInAttempt = 0;
export function openSignIn({ onSignedIn, onBack, onNew } = {}) {
  const { p, wrap } = page('acctSignIn');
  preloadCues();
  const attempt = ++signInAttempt;
  let polling = true;
  const live = () => polling && attempt === signInAttempt;
  const close = () => { polling = false; fadeRemove(p); };
  // adopt the obtained credentials only if this attempt still stands; returns whether it did
  const finish = (creds) => {
    if (!live()) return false;
    identity.adoptAccount(creds);
    cue('deckready', 0.85);
    close(); onSignedIn?.(identity.currentPlayer());
    return true;
  };
  const top = el('div', 'acct-top');
  const back = el('button', 'acct-link', '‹ Back'); back.type = 'button';
  back.addEventListener('click', () => { close(); onBack?.(); });
  top.append(back, el('span'));
  const h = el('h2', 'acct-h', 'Welcome back');
  const sub = el('p', 'acct-sub', 'Your cards and progress come with you.');

  // (a) name + PIN
  const a = el('div', 'acct-pinstep');
  const nm = el('input', 'acct-in'); Object.assign(nm, { placeholder: 'Your name', autocomplete: 'username', maxLength: 16 });
  nm.setAttribute('autocapitalize', 'words'); nm.setAttribute('autocorrect', 'off'); nm.spellcheck = false;
  const pin = el('input', 'acct-in acct-pin'); Object.assign(pin, { placeholder: '••••', inputMode: 'numeric', maxLength: 4, autocomplete: 'current-password', type: 'password' });
  pin.setAttribute('pattern', '[0-9]*');
  const err = el('div', 'acct-err');
  const sign = el('button', 'acct-btn', 'Sign in'); sign.type = 'button';
  const doSign = async () => {
    err.textContent = '';
    if (!nm.value.trim() || !/^\d{4}$/.test(pin.value)) { err.textContent = 'Your name and your four-digit PIN'; return; }
    sign.disabled = true;
    try { finish(await identity.pinCredentials(nm.value.trim(), pin.value)); }
    catch (e) {
      if (!live()) return;
      const b = e?.body || {};
      if (isNetErr(e)) { netErr(err, doSign); return; }
      err.textContent = b.code === 'PIN_LOCKED' ? `Too many wrong PINs. Try again in ${fmtLeft(b.retryAfterMs || 60000)}`
        : b.code === 'BAD_LOGIN' ? `That name and PIN do not match${b.triesLeft != null ? ` · ${b.triesLeft} tries left` : ''}`
        : "Can't reach the arena. Try again";
      pin.value = '';
    } finally { sign.disabled = false; }
  };
  sign.addEventListener('click', doSign);
  pin.addEventListener('keydown', (e) => { if (e.key === 'Enter') doSign(); });
  a.append(nm, pin, err, sign, el('div', 'acct-small', 'No PIN yet? Set one in Account on your other device.'));

  // (b) this device's own code for a signed-in device to approve
  const b = el('div', 'acct-scanstep');
  let qr = qrBox(null);
  const code = el('div', 'acct-code', '····-····');
  const how = el('p', null, 'On your other device, open Account and scan this, or type the code.');
  const st = el('div', 'acct-small', 'Getting a code…');
  b.append(qr, code, how, st);
  const steps = stepper([{ key: 'scan', label: 'Scan', node: b }, { key: 'pin', label: 'Name + PIN', node: a }], 'scan');
  const foot = el('button', 'acct-link', 'No other device? Start a new account'); foot.type = 'button';
  foot.addEventListener('click', () => { close(); onNew(); });
  if (!onNew) foot.hidden = true;   // (a caller without a new-account path shows no dead action)
  wrap.append(top, h, sub);
  const here = identity.storedAccounts();
  const rowErr = el('div', 'acct-err');
  if (here.length) {
    const row = el('div', 'acct-row acct-here');
    row.appendChild(el('span', 'acct-small', 'On this device'));
    for (const n of here) {
      const btn = el('button', 'acct-btn2', n); btn.type = 'button';
      const use = async () => { btn.disabled = true; try { finish(await identity.storedCredentials(n)); } catch (e) { btn.disabled = false; if (live() && isNetErr(e) && e?.message !== 'Not on this device') netErr(rowErr, use); } };
      btn.addEventListener('click', use);
      row.appendChild(btn);
    }
    wrap.append(row, rowErr);
  }
  wrap.append(steps.bar, b, a, foot);

  (async () => {
    let pair = null;
    const fresh = async () => {
      try {
        pair = await identity.startPairing();
        const nq = qrBox(pair.qr); qr.replaceWith(nq); qr = nq;
        code.textContent = pair.code;
        st.textContent = `Waiting for approval · ${fmtLeft(pair.expiresTs - Date.now())}`;
      } catch { st.textContent = "Can't reach the arena right now"; pair = null; }
    };
    await fresh();
    while (live()) {
      await new Promise(r => setTimeout(r, 3000));
      if (!live()) break;
      if (!pair) { await fresh(); continue; }
      if (Date.now() > pair.expiresTs) { await fresh(); continue; }
      st.textContent = `Waiting for approval · ${fmtLeft(pair.expiresTs - Date.now())}`;
      try {
        const j = await identity.pairCredentials(pair.id, pair.secret);
        if (!live()) break;                       // dismissed while the poll was in flight: drop the answer
        if (j.status === 'approved') {
          st.textContent = `Signed in as ${j.name}`; st.classList.add('acct-ok');
          setTimeout(() => finish(j), 700);       // adopted only if Back was not tapped in the meantime
          return;
        }
        if (j.status === 'expired') await fresh();
      } catch { /* keep polling */ }
    }
  })();
  return close;
}

/** LINK: optional, standalone. `first` = the one automatic showing (after the first win): big SKIP.
 *  polB: three steps behind a segmented control -- LINK (the one-time QR as the hero + Send to myself), APPROVE
 *  (another device's code) and PIN. ?pair= opens on APPROVE. */
export function openLinkPage({ first = false, approveCode = null, onClose, onSwitch } = {}) {
  if (!identity.currentPlayer()) return null;
  const { p, wrap } = page('acctLink');
  preloadCues();
  let alive = true, timer = 0;
  const close = () => { alive = false; clearInterval(timer); fadeRemove(p); try { audio.ensureMusic(); } catch { /* */ } onClose?.(); };
  const top = el('div', 'acct-top');
  const skip = el('button', first ? 'acct-skip' : 'acct-link', first ? 'Skip' : '‹ Back'); skip.type = 'button';
  skip.addEventListener('click', close);
  const sw = el('button', 'acct-link', 'Switch player'); sw.type = 'button';
  sw.addEventListener('click', () => { close(); onSwitch?.(); });
  top.append(skip, first ? el('span') : sw);
  const name = identity.currentPlayer();
  const h = el('h2', 'acct-h', first ? `Keep ${name} safe` : 'Play anywhere');
  const sub = el('p', 'acct-sub', first ? 'Optional. Link a device or set a PIN, now or later in Account.'
    : `Signed in as ${name}`);

  // (a) a one-time link for another device
  const a = el('div', 'acct-linkstep');
  let qr = qrBox(null);
  const left = el('div', 'acct-small', '');
  const send = el('button', 'acct-btn', 'Send to myself'); send.type = 'button'; send.disabled = true;
  const again = el('button', 'acct-link', 'New link'); again.type = 'button';
  a.append(qr, el('p', null, 'Scan with your other device. Works once, for 10 minutes.'), left, send, again);
  let link = null;
  const mint = async () => {
    send.disabled = true; left.textContent = 'Making a link…';
    try {
      link = await identity.mintLoginLink();
      const nq = qrBox(link.qr); qr.replaceWith(nq); qr = nq;
      send.disabled = !link.url;
    } catch { left.textContent = "Can't reach the arena right now"; link = null; }
  };
  timer = setInterval(() => {
    if (!alive || !link) return;
    const ms = link.expiresTs - Date.now();
    left.textContent = ms > 0 ? `Expires in ${fmtLeft(ms)}` : 'Expired — make a new link';
    if (ms <= 0) send.disabled = true;
  }, 500);
  again.addEventListener('click', mint);
  send.addEventListener('click', async () => {
    if (!link?.url) return;
    const text = `My Nomad-mon login (works once, 10 min): ${link.url}`;
    try {
      if (navigator.share) { await navigator.share({ title: 'Nomad-mon Go!', text: 'My Nomad-mon login (works once, 10 min)', url: link.url }); return; }
    } catch (e) { if (e?.name === 'AbortError') return; }
    open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  });
  mint();

  // approve a NEW device's code (its sign-in page shows it)
  const ap = el('div', 'acct-approvestep');
  const codeIn = el('input', 'acct-in'); Object.assign(codeIn, { placeholder: 'XXXX-XXXX', maxLength: 9, value: approveCode ? `${approveCode.slice(0, 4)}-${approveCode.slice(4)}` : '' });
  codeIn.setAttribute('autocapitalize', 'characters'); codeIn.setAttribute('autocorrect', 'off'); codeIn.spellcheck = false;
  codeIn.style.letterSpacing = '.12em';
  const apBtn = el('button', 'acct-btn', 'Approve'); apBtn.type = 'button';
  const apMsg = el('div', 'acct-err');
  apBtn.addEventListener('click', async () => {
    apMsg.textContent = ''; apMsg.classList.remove('acct-ok');
    try { await identity.approvePairing(codeIn.value); apMsg.textContent = `Approved. The other device is now ${name}.`; apMsg.classList.add('acct-ok'); codeIn.value = ''; cue('tradecomplete', 0.5); }
    catch (e) { apMsg.textContent = e?.body?.error || "That code didn't work"; }
  });
  ap.append(el('p', null, approveCode ? `Sign the other device in as ${name}?` : 'Type the code your new device shows.'));
  if (approveCode) {
    const warn = el('div', 'acct-warn');
    warn.append(el('b', null, 'Only approve if you are holding the other device right now. '),
      document.createTextNode(`Approving signs that device in as ${name}. If someone sent you this link, do not approve: they would get your account.`));
    ap.append(warn);
  }
  ap.append(codeIn, apBtn, apMsg);

  // (b) the PIN
  const b = el('div', 'acct-pinstep');
  const pinState = el('div', 'acct-small', '');
  const pin = el('input', 'acct-in acct-pin'); Object.assign(pin, { placeholder: '••••', inputMode: 'numeric', maxLength: 4, autocomplete: 'new-password', type: 'password' });
  pin.setAttribute('pattern', '[0-9]*');
  const curPin = el('input', 'acct-in acct-pin'); Object.assign(curPin, { placeholder: 'old', inputMode: 'numeric', maxLength: 4, autocomplete: 'current-password', type: 'password' });
  curPin.hidden = true;
  const save = el('button', 'acct-btn', 'Save PIN'); save.type = 'button';
  const pinMsg = el('div', 'acct-err');
  const devs = el('div', 'acct-small', '');
  const out = el('button', 'acct-link', 'Sign out other devices'); out.type = 'button'; out.hidden = true;
  b.append(el('p', null, 'Four digits. Sign in anywhere with your name + PIN.'), curPin, pin, save, pinMsg, pinState, devs, out);
  const paintInfo = () => identity.accountInfo().then((j) => {
    if (!alive) return;
    if (j.hasPin) { pinState.textContent = 'You have a PIN · type the old one, then the new one'; save.textContent = 'Change PIN'; curPin.hidden = false; pin.placeholder = 'new'; }
    devs.textContent = j.devices > 1 ? `Signed in on ${j.devices} devices` : 'Signed in on this device only';
    out.hidden = !(j.devices > 1);
  }).catch(() => {});
  paintInfo();
  out.addEventListener('click', async () => {
    out.disabled = true;
    try { const r = await identity.revokeOtherDevices(); devs.textContent = `Signed out ${r.signedOut} other device${r.signedOut === 1 ? '' : 's'}`; out.hidden = true; }
    catch { devs.textContent = "Couldn't sign them out. Try again"; }
    finally { out.disabled = false; }
  });
  save.addEventListener('click', async () => {
    pinMsg.textContent = ''; pinMsg.classList.remove('acct-ok');
    if (!/^\d{4}$/.test(pin.value)) { pinMsg.textContent = 'Four digits'; return; }
    save.disabled = true;
    try { await identity.setAccountPin(pin.value, curPin.hidden ? undefined : curPin.value); pinMsg.textContent = 'PIN saved ✓'; pinMsg.classList.add('acct-ok'); cue('deckready', 0.6); pin.value = ''; curPin.value = ''; save.disabled = true; setTimeout(() => { if (alive) close(); }, 1300); return; }
    catch (e) { pinMsg.textContent = e?.body?.code === 'PIN_CURRENT_REQUIRED' ? 'Your old PIN is not right' : "Couldn't save. Try again"; }
    finally { save.disabled = false; }
  });
  const steps = stepper([{ key: 'link', label: 'Link', node: a }, { key: 'approve', label: 'Approve', node: ap },
    { key: 'pin', label: 'PIN', node: b }], approveCode ? 'approve' : 'link');
  wrap.append(top, h, sub, steps.bar, a, ap, b);
  if (first) {
    const skip2 = el('button', 'acct-skip', 'Skip for now'); skip2.type = 'button';
    skip2.addEventListener('click', close);
    wrap.append(skip2);
  }
  return close;
}

// ======================================================================= THE ONE AUTOMATIC SHOWING
// After the player's FIRST WIN (a battle win on their record, or any campaign star), the next return to
// the title opens the LINK page once, with a big SKIP. Per name, per device; never again after.
const offerKey = (n) => `nomadmon-link-offered:${String(n).toLowerCase()}`;
function firstWinDue() {
  const n = identity.currentPlayer();
  if (!n) return false;
  try { if (localStorage.getItem(offerKey(n))) return false; } catch { return false; }
  const wins = identity.record?.()?.wins || 0;
  const stars = Object.values(identity.campaignProgress?.() || {}).some((s) => Number(s) > 0);
  return wins > 0 || stars;
}
function watchFirstWin() {
  let pending = 0;
  const check = () => {
    if (document.body.dataset.screen !== 'title' || document.body.classList.contains('entry-active')) return;
    if ($('acctLink') || $('acctSignIn') || forge || !firstWinDue()) return;
    clearTimeout(pending);
    pending = setTimeout(() => {
      if (document.body.dataset.screen !== 'title' || !firstWinDue() || $('acctLink')) return;
      try { localStorage.setItem(offerKey(identity.currentPlayer()), String(Date.now())); } catch { /* */ }
      openLinkPage({ first: true, onSwitch: hooks.onSwitch });
    }, 1400);
  };
  new MutationObserver(check).observe(document.body, { attributes: true, attributeFilter: ['data-screen', 'class'] });
}

/** ?pair=CODE (a new device's QR, scanned by this signed-in device): offer to approve it. */
function consumePairParam() {
  let code = null;
  try { code = new URLSearchParams(location.search).get('pair'); } catch { /* */ }
  if (!code) return;
  try { const u = new URL(location.href); u.searchParams.delete('pair'); history.replaceState(history.state, '', u); } catch { /* */ }
  const clean = String(code).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  if (clean.length !== 8) return;
  const show = () => {
    if (identity.currentPlayer()) openLinkPage({ approveCode: clean, onSwitch: hooks.onSwitch });
    else hooks.notify?.('Sign in on this device first, then approve the code from Menu → Account.', 8000);
  };
  // after the front door has handed off (the entry owns the screen until then)
  const wait = () => (document.body.classList.contains('entry-active') ? setTimeout(wait, 500) : setTimeout(show, 600));
  wait();
}

// ======================================================================= ACCOUNT LOST / OFFLINE
// identity.js signs the device out the moment the server says its account is gone; play stops here (an
// overlay over everything) and a fresh load goes back to the name entry, which says why. A network outage
// only shows a small pill: play goes on, but the player knows progress is not being saved.
function onAccountLost() {
  try { sessionStorage.setItem('nomadmon-account-lost', '1'); } catch { /* private */ }
  if ($('acctLost')) return;
  forge?.close?.();
  const { p, wrap } = page('acctLost');
  p.id = 'acctLost';
  wrap.style.justifyContent = 'center';
  const card = el('div', 'acct-card');
  const btn = el('button', 'acct-btn', 'Make a new one'); btn.type = 'button';
  btn.addEventListener('click', () => location.reload());
  card.append(el('h3', null, 'Your account was reset'),
    el('p', null, 'This device is signed out, so nothing from here on would be saved.'), btn);
  wrap.append(card);
  try { audio.stopMusic?.({ fade: 0.6 }); } catch { /* */ }
  setTimeout(() => btn.focus(), 50);
}
function watchAccountHealth() {
  addEventListener('nomadmon-account-lost', onAccountLost);
  // polB: ONE app-wide offline pill. Down = the browser says offline ('offline' / 'online' events, and
  // navigator.onLine at boot) OR the API is unreachable (identity's 'nomadmon-net'). It slides in / out.
  const pill = el('div', null, 'Offline · progress not saving'); pill.id = 'acctNet'; pill.hidden = true;
  pill.setAttribute('role', 'status');
  document.body.appendChild(pill);
  let retry = 0, apiDown = false, browserOff = navigator.onLine === false, outT = 0;
  const paint = () => {
    const down = apiDown || browserOff;
    clearTimeout(outT);
    if (down) { pill.classList.remove('out'); pill.hidden = false; return; }
    if (pill.hidden) return;
    pill.classList.add('out');
    outT = setTimeout(() => { pill.hidden = true; pill.classList.remove('out'); }, 240);
  };
  addEventListener('nomadmon-net', (e) => {
    const down = !!e.detail?.down;
    apiDown = down;
    paint();
    clearInterval(retry);
    if (down) retry = setInterval(() => { identity.checkAccountAlive(); }, 20_000);   // any answer clears it
  });
  addEventListener('offline', () => { browserOff = true; paint(); });
  addEventListener('online', () => { browserOff = false; paint(); identity.checkAccountAlive(); });   // re-ask the API at once
  paint();
  // an idle tab (or one coming back to the front) asks whether its account still exists
  const check = () => { if (identity.currentPlayer() && document.visibilityState === 'visible') identity.checkAccountAlive(); };
  document.addEventListener('visibilitychange', check);
  setInterval(check, 3 * 60_000);
}

export function initAccount(h = {}) {
  hooks = { ...hooks, ...h };
  injectStyle();
  watchAccountHealth();
  watchFirstWin();
  consumePairParam();
}
